/**
 * 🗄️ Employee Management data cache (Manager view)
 *
 * React state resets every time this page unmounts (switching tabs,
 * navigating away and back, etc.), which previously forced a brand new
 * round-trip for employees/hierarchy/counts/companies/archived on every
 * single visit — even if the user had loaded the exact same data seconds
 * earlier. It also meant every hierarchy expand/collapse click re-fetched
 * the entire employee list from the network just to re-flatten it. This
 * module lives outside the React tree so it survives unmount/remount
 * within the same browser session and lets callers re-derive local view
 * state (like the flattened hierarchy) from cached data without hitting
 * the network again.
 *
 * - Fresh (within TTL) results are served instantly from memory instead of
 *   hitting the network again.
 * - Concurrent requests for the same key are deduplicated — only one
 *   network call goes out even if multiple hooks ask for the same data at
 *   the same time.
 * - Callers pass `force: true` after a mutation (add/edit/delete/archive/
 *   restore) to bypass the cache and guarantee fresh data.
 */

const DEFAULT_TTL_MS = 30 * 1000; // 30s — short enough to stay accurate, long enough to kill duplicate fetches on remount

const store = new Map(); // key -> { data, expiresAt }
const inFlight = new Map(); // key -> Promise

/**
 * Fetch `key`, transparently caching the result.
 *
 * @param {string} key - stable cache key (include clientName/viewType/etc.)
 * @param {() => Promise<any>} fetcher - performs the actual network request
 * @param {{ ttl?: number, force?: boolean }} [options]
 */
export function getCached(key, fetcher, { ttl = DEFAULT_TTL_MS, force = false } = {}) {
  if (!force) {
    const cached = store.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return Promise.resolve(cached.data);
    }
    const pending = inFlight.get(key);
    if (pending) return pending;
  }

  const promise = Promise.resolve()
    .then(fetcher)
    .then((data) => {
      store.set(key, { data, expiresAt: Date.now() + ttl });
      inFlight.delete(key);
      return data;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });

  inFlight.set(key, promise);
  return promise;
}

/**
 * Drop cached entries. With no prefix, clears everything; otherwise clears
 * only keys starting with `prefix` (e.g. invalidateEmployeeCache("employees:")).
 */
export function invalidateEmployeeCache(prefix) {
  const keys = new Set([...store.keys(), ...inFlight.keys()]);
  keys.forEach((key) => {
    if (!prefix || key.startsWith(prefix)) {
      store.delete(key);
      inFlight.delete(key);
    }
  });
}
